import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';

/**
 * THE PRODUCTION DYNAMAN DIALOG — a scaffold, deliberately.
 *
 * <h2>Why this exists at all</h2>
 *
 * The research dialog has sixteen CTS exit levers, four TrendPulse detector levers, a provider
 * selector, a direction rule, three isolation switches and a stepped replay. Every one of those
 * is a question about how the engine should be CONFIGURED, which is a researcher's question. A
 * production reader is asking a different one: what does this engine say about this symbol.
 *
 * <p>So the split is by audience, not by feature count. Admin keeps the research dialog; ASC and
 * AI reach this one, which will eventually present a finished run rather than the controls that
 * produce it.
 *
 * <h2>It is honest about being unfinished</h2>
 *
 * A placeholder that looked like a product would be worse than an empty one: a reader who could
 * not tell the difference would report missing features as bugs. It says what it is, names the
 * one-click engine icons that DO work today, and offers nothing it cannot do.
 *
 * <p>The layout is deliberately not designed yet — that was the instruction, and guessing at it
 * now would mean throwing the guess away.
 */
const DynaManProductionDialog = ({ open, onClose, symbol = '', profile = '' }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ pb: 0.5 }}>
      DynaMan
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {symbol ? `${symbol} — ` : ''}production view
        {profile ? ` (${profile} profile)` : ''}
      </Typography>
    </DialogTitle>
    <DialogContent>
      <Alert severity="info" sx={{ mb: 2 }} data-testid="production-scaffold-notice">
        This view is a scaffold. The production layout has not been designed yet.
      </Alert>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        The research dialog — provider selection, exit-strategy levers, the stepped replay and
        the signal audit — is restricted to the Admin profile. It configures the engine; this
        view will present what the engine found.
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>The engines already run from the toolbar.</strong> The three run icons execute
        PTA, PTB and CTS with their locked parameters and paint the entries and exits straight
        onto the chart — no configuration, one click.
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, m: 0, color: 'text.secondary' }}>
        <Typography component="li" variant="caption">
          PTA — the Equator engine
        </Typography>
        <Typography component="li" variant="caption">
          PTB — the eight band rules
        </Typography>
        <Typography component="li" variant="caption">
          CTS — the temporal pivot engine
        </Typography>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

DynaManProductionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** The chart's symbol, shown so the reader knows which instrument this is about. */
  symbol: PropTypes.string,
  /** Named in the subtitle, so a dev flipping profiles can see which one produced this view. */
  profile: PropTypes.string,
};

export default DynaManProductionDialog;
